import React, { useMemo } from 'react';
import { useCounterOfferStore } from '@/stores/counterOfferStore';
import { PoolDetail } from '@/types/pool';
import { DOCUMENT_TYPE_CONFIG, DocumentType } from '@/types/pool/documentTypes';
import { Button } from '../../ui/Button';
import { CheckboxWithLabel } from '../../ui/CheckboxWithLabel';

type CounterOfferDocumentsStepProps = {
  pool: PoolDetail;
};

export const CounterOfferDocumentsStep: React.FC<CounterOfferDocumentsStepProps> = ({ pool }) => {
  const nextStep = useCounterOfferStore((state) => state.nextStep);
  const previousStep = useCounterOfferStore((state) => state.previousStep);
  const isFirstStep = useCounterOfferStore((state) => state.isFirstStep);
  const requiredDocuments = useCounterOfferStore((state) => state.requiredDocuments);
  const setRequiredDocuments = useCounterOfferStore((state) => state.setRequiredDocuments);

  // Get existing document types in the pool (excluding photos)
  const existingDocTypes = useMemo(() => {
    return new Set(
      pool.documents?.filter((doc) => doc.doc_type !== DocumentType.PHOTO).map((doc) => doc.doc_type) || []
    );
  }, [pool.documents]);

  // Get all available document types (excluding photos)
  const availableDocTypes = useMemo(() => {
    return Object.values(DocumentType).filter(
      (type) => type !== DocumentType.PHOTO && type !== DocumentType.CO_OWNER_ID
    );
  }, []);

  // Get missing documents (documents not in the pool)
  const missingDocuments = useMemo(() => {
    return availableDocTypes.filter((type) => !existingDocTypes.has(type));
  }, [availableDocTypes, existingDocTypes]);

  // Get existing photos
  const existingPhotos = useMemo(() => {
    return pool.documents?.filter((doc) => doc.doc_type === DocumentType.PHOTO) || [];
  }, [pool.documents]);

  const handleDocumentToggle = (docType: string) => {
    const currentDocs = [...requiredDocuments];
    const index = currentDocs.indexOf(docType);
    if (index > -1) {
      currentDocs.splice(index, 1);
    } else {
      currentDocs.push(docType);
    }
    setRequiredDocuments(currentDocs);
  };

  return (
    <div className="p-8 flex flex-col gap-6">
      {/* Documents available to request */}
      <div className="flex flex-col gap-4">
        <div
          className="text-black text-base font-semibold"
          style={{
            fontFamily: 'var(--ep-font-avenir)',
          }}
        >
          Documents available to request
        </div>
        <div
          className="text-gray-600 text-sm"
          style={{
            fontFamily: 'var(--ep-font-avenir)',
          }}
        >
          Select the documents you would like to be added to the pool.
        </div>
        <div className="flex flex-col gap-2">
          {missingDocuments.length > 0 ? (
            missingDocuments.map((docType) => (
              <CheckboxWithLabel
                key={docType}
                checked={requiredDocuments.includes(docType)}
                onChange={() => handleDocumentToggle(docType)}
                label={DOCUMENT_TYPE_CONFIG[docType].label}
              />
            ))
          ) : (
            <div
              className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg"
              style={{
                fontFamily: 'var(--ep-font-avenir)',
              }}
            >
              All available documents are already in the pool.
            </div>
          )}
        </div>
      </div>

      {/* Property Photos */}
      <div className="flex flex-col gap-4">
        <div
          className="text-black text-base font-semibold"
          style={{
            fontFamily: 'var(--ep-font-avenir)',
          }}
        >
          Property Photos (JPG, PNG)
        </div>
        {existingPhotos.length > 0 && (
          <div className="grid grid-cols-8 gap-2">
            {existingPhotos.map((photo, index) => (
              <div
                key={photo.url || index}
                className="w-full aspect-square bg-gray-100 rounded-lg border border-gray-200 relative overflow-hidden cursor-pointer"
                onClick={() => window.open(photo.url, '_blank')}
              >
                <img
                  src={photo.url}
                  alt={`Property photo ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const placeholder = target.parentElement?.querySelector('.placeholder');
                    if (placeholder) {
                      (placeholder as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div
                  className="placeholder w-full h-full bg-gradient-to-br from-[#E4EFFF] to-[#F4F4F4] hidden justify-center items-center"
                  style={{ display: 'none' }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
                      fill="#B2B2B2"
                    />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
        <CheckboxWithLabel
          checked={requiredDocuments.includes(DocumentType.PHOTO)}
          onChange={() => handleDocumentToggle(DocumentType.PHOTO)}
          label="Request more photos"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-end pt-4 border-t border-gray-200">
        {!isFirstStep() && (
          <Button onClick={previousStep} variant="secondary">
            Previous
          </Button>
        )}
        <Button onClick={nextStep} variant="primary">
          Next
        </Button>
      </div>
    </div>
  );
};
